/** Parse "Name <email@x.com>" or bare email into name parts + address. */
export function parseFromAddress(raw: string): {
  email: string;
  first_name: string;
  last_name: string;
  display: string;
} {
  const s = (raw || '').trim();
  if (!s) {
    return { email: '', first_name: '', last_name: '', display: '' };
  }

  let name = '';
  let email = '';
  const angled = s.match(/^(.*?)\s*<([^>]+)>\s*$/);
  if (angled) {
    name = angled[1].replace(/^["']|["']$/g, '').trim();
    email = angled[2].trim();
  } else if (s.includes('@')) {
    email = s;
  } else {
    return { email: '', first_name: '', last_name: '', display: s };
  }

  const parts = name.split(/\s+/).filter(Boolean);
  const first_name = parts[0] || '';
  const last_name = parts.slice(1).join(' ');
  return {
    email: email.toLowerCase(),
    first_name,
    last_name,
    display: name || email,
  };
}

/** Split a To/Cc/Bcc field into bare email addresses. */
export function parseAddressList(raw: string): string[] {
  return (raw || '')
    .split(',')
    .map((part) => parseFromAddress(part).email)
    .filter(Boolean);
}

/** Flatten DRF / proxy error payloads into a readable string. */
export function formatApiError(data: unknown, fallback = 'Request failed.'): string {
  if (!data) return fallback;
  if (typeof data === 'string') {
    const lower = data.toLowerCase();
    if (lower.includes('504') || lower.includes('gateway time-out') || lower.includes('gateway timeout')) {
      return 'Request timed out (504). Plain send usually means SMTP is slow/unreachable; Sign/Encrypt timeouts are often GnuPG. Turn Sign/Encrypt off to test SMTP.';
    }
    if (data.includes('<html') || data.includes('<HTML')) {
      return fallback;
    }
    return data;
  }
  if (typeof data !== 'object') return fallback;
  const obj = data as Record<string, unknown>;
  if (typeof obj.detail === 'string') return obj.detail;
  if (Array.isArray(obj.detail)) return obj.detail.map(String).join(' ');
  const parts: string[] = [];
  for (const [key, val] of Object.entries(obj)) {
    if (key === 'detail') continue;
    if (Array.isArray(val)) parts.push(`${key}: ${val.map(String).join(' ')}`);
    else if (typeof val === 'string') parts.push(`${key}: ${val}`);
  }
  return parts.length ? parts.join('; ') : fallback;
}

/** Prefer status-aware messages for compose/send failures. */
export function formatSendError(err: unknown, usedGpg: boolean): string {
  const anyErr = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = anyErr?.response?.status;
  if (status === 504 || status === 502) {
    return usedGpg
      ? 'Timed out during Sign/Encrypt (GnuPG). Check recipient public keys and passphrase, or turn Sign/Encrypt off.'
      : 'Timed out sending mail (SMTP). Check SMTP host/port/firewall; the server did not accept the message in time.';
  }
  return formatApiError(anyErr?.response?.data, anyErr?.message || 'Failed to send.');
}
